import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Navbar from "./Navbar";
import "./AdminDashboard.css";
import AdSubmit from "./AdSubmit";
import AdsAnalytics from "./AdsAnalytics";
import AdminAdCampaigns from "./Ads/AdminAdCampaigns";
import AdminLove from "./AdminLove";
import AdminSalesExecutives from "./AdminSalesExecutives";

function AdminDashboard() {
  const [users, setUsers]     = useState([]);
  const [blogs, setBlogs]     = useState([]);
  const [rewards, setRewards] = useState([]);

  const [userPage, setUserPage] = useState(1);
  const [blogPage, setBlogPage] = useState(1);
  const usersPerPage = 8;
  const blogsPerPage = 8;

  const [userSearch, setUserSearch] = useState("");
  const [blogSearch, setBlogSearch] = useState("");

  // ── ANNOUNCEMENT (targeted / broadcast push) ──
  const [annTitle, setAnnTitle] = useState("");
  const [annBody, setAnnBody]   = useState("");
  const [annLink, setAnnLink]   = useState("");
  const [annSending, setAnnSending] = useState(false);
  const [annResult, setAnnResult]   = useState("");
  // Recipient selection: broadcast to everyone, or a set of specific user ids.
  const [annSendToAll, setAnnSendToAll] = useState(true);
  const [annSelectedIds, setAnnSelectedIds] = useState([]);
  const [annUserSearch, setAnnUserSearch] = useState("");

  const BASE_URL =
    window.location.hostname === "localhost"
      ? "http://localhost:5000"
      : "https://success-blog-app-backend.onrender.com";

  const token = localStorage.getItem("token");

  const api = axios.create({
    baseURL: BASE_URL,
    headers: { Authorization: `Bearer ${token}` },
  });

  // ── FETCH ──
  const fetchUsers = async () => {
    try {
      const res = await api.get("/api/admin/users");
      setUsers(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchBlogs = async () => {
    try {
      const res = await api.get("/api/admin/blogs");
      console.log("Blogs Data:", res.data[0]); // Debug kosam - streak field check chey
      setBlogs(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchRewards = async () => {
    try {
      const res = await api.get("/api/reward");
      setRewards(res.data);
    } catch (err) { console.error(err); }
  };

  useEffect(() => {
    fetchUsers();
    fetchBlogs();
    fetchRewards();
  }, []);

  // ── USER ACTIONS ──
  const toggleBlock = async (id) => {
    try {
      await api.put(`/api/admin/users/${id}/block`);
      fetchUsers();
    } catch (err) { console.error(err); }
  };

  const deleteUser = async (id) => {
    if (!window.confirm("Delete user and all their blogs?")) return;
    try {
      await api.delete(`/api/admin/users/${id}`);
      fetchUsers();
      fetchBlogs();
    } catch (err) { console.error(err); }
  };

  // ── BLOG ACTIONS ──
  const deleteBlog = async (id) => {
    if (!window.confirm("Delete this blog?")) return;
    try {
      await api.delete(`/api/admin/blogs/${id}`);
      fetchBlogs();
    } catch (err) { console.error(err); }
  };

  // ── REWARD ACTIONS ──
  const addReward = async (blogId) => {
    const amount = prompt("Enter reward amount (₹)");
    if (!amount || isNaN(amount)) return;
    try {
      await api.post("/api/reward/add", { blogId, amount: parseInt(amount) });
      fetchRewards();
    } catch (err) { console.error(err); }
  };

  const deleteReward = async (rewardId) => {
    if (!window.confirm("Remove this reward?")) return;
    try {
      await api.delete(`/api/reward/delete/${rewardId}`);
      fetchRewards();
    } catch (err) { console.error(err); }
  };

  // ── ANNOUNCEMENT RECIPIENT HELPERS ──
  // Users matching the recipient-picker search box.
  const annFilteredUsers = users.filter((u) =>
    (u.name || "").toLowerCase().includes(annUserSearch.toLowerCase()) ||
    (u.email || "").toLowerCase().includes(annUserSearch.toLowerCase())
  );

  const toggleAnnUser = (id) => {
    setAnnSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // A broadcast ("Send to everyone") reaches only ACTIVE users — blocked
  // accounts are excluded server-side, so the count/label must reflect that.
  const annActiveCount = users.filter((u) => !u.blocked).length;

  // Number of recipients the send will actually reach.
  const annRecipientCount = annSendToAll ? annActiveCount : annSelectedIds.length;

  // Send is only allowed with a message AND at least one recipient.
  const annCanSend =
    !annSending && !!annBody.trim() && annRecipientCount > 0;

  // ── ANNOUNCEMENT ACTION ──
  // Sends a push notification to the selected recipients (or everyone) and
  // persists it so each recipient sees it in their in-app notifications.
  const sendAnnouncement = async () => {
    if (!annBody.trim()) {
      setAnnResult("Please enter an announcement message.");
      return;
    }
    if (!annSendToAll && annSelectedIds.length === 0) {
      setAnnResult("Select at least one recipient.");
      return;
    }
    setAnnSending(true);
    setAnnResult("");
    try {
      const res = await api.post("/api/admin/announcement", {
        title: annTitle.trim(),
        body: annBody.trim(),
        link: annLink.trim(),
        sendToAll: annSendToAll,
        userIds: annSendToAll ? [] : annSelectedIds,
      });
      setAnnResult(
        `✅ Sent to ${res.data.recipients} user(s) — delivered to ${res.data.delivered} device(s)` +
          (res.data.failed ? `, ${res.data.failed} failed` : "")
      );
      setAnnTitle("");
      setAnnBody("");
      setAnnLink("");
      setAnnSelectedIds([]);
      setAnnUserSearch("");
    } catch (err) {
      setAnnResult(
        `❌ ${err.response?.data?.message || "Failed to send announcement"}`
      );
    } finally {
      setAnnSending(false);
    }
  };

  // ── REWARD HELPERS ──
  const getBlogReward = (blogId) =>
    rewards.filter(r => r.blogId === blogId).reduce((s, r) => s + r.amount, 0);

  const getRewardId = (blogId) =>
    rewards.find(r => r.blogId === blogId)?._id;

  // ── STREAK HELPER - MULTIPLE FALLBACKS ──
  const getBlogStreak = (blog) => {
    // 1. Direct blog.streak
    if (blog.streak !== undefined && blog.streak !== null) return blog.streak;
    
    // 2. streakCount
    if (blog.streakCount !== undefined && blog.streakCount !== null) return blog.streakCount;
    
    // 3. currentStreak
    if (blog.currentStreak !== undefined && blog.currentStreak !== null) return blog.currentStreak;
    
    // 4. Author object lo streak
    if (blog.author?.streak !== undefined && blog.author?.streak !== null) return blog.author.streak;
    
    // 5. Users array lo find chesi streak teesuko
    const authorId = blog.author?._id || blog.author;
    const authorUser = users.find(u => u._id === authorId);
    if (authorUser?.streak !== undefined && authorUser?.streak !== null) return authorUser.streak;
    
    // 6. Default 0
    return 0;
  };

  // ── AUTHOR NAME HELPER ──
  const getAuthorName = (author) => {
    if (!author) return "—";
    if (typeof author === "string") {
      const user = users.find(u => u._id === author);
      return user?.name || author;
    }
    return author.name || author._id || "—";
  };

  // ── FILTERED DATA ──
  const filteredUsers = users.filter(u =>
    (u.name  || "").toLowerCase().includes(userSearch.toLowerCase()) ||
    (u.email || "").toLowerCase().includes(userSearch.toLowerCase())
  );

  const filteredBlogs = blogs.filter(b =>
    (b.title || "").toLowerCase().includes(blogSearch.toLowerCase()) ||
    getAuthorName(b.author).toLowerCase().includes(blogSearch.toLowerCase())
  );

  // ── PAGINATION ──
  const totalUserPages = Math.ceil(filteredUsers.length / usersPerPage);
  const totalBlogPages = Math.ceil(filteredBlogs.length / blogsPerPage);
  const pagedUsers = filteredUsers.slice((userPage - 1) * usersPerPage, userPage * usersPerPage);
  const pagedBlogs = filteredBlogs.slice((blogPage - 1) * blogsPerPage, blogPage * blogsPerPage);

  // ── STATS ──
  const totalBlocked = users.filter(u => u.blocked).length;
  const totalLikes   = blogs.reduce((s, b) => s + (b.likes?.length || 0), 0);
  const totalRewards = rewards.reduce((s, r) => s + r.amount, 0);

  const Pagination = ({ page, total, onChange }) => (
    total > 1 ? (
      <div className="admin-pagination">
        <button className="admin-page-btn" disabled={page === 1} onClick={() => onChange(page - 1)}>← Prev</button>
        {[...Array(total)].map((_, i) => (
          <button
            key={i}
            className={`admin-page-btn ${page === i + 1 ? "admin-page-active" : ""}`}
            onClick={() => onChange(i + 1)}
          >{i + 1}</button>
        ))}
        <button className="admin-page-btn" disabled={page === total} onClick={() => onChange(page + 1)}>Next →</button>
      </div>
    ) : null
  );

  return (
    <div className="admin-root">
      <Navbar />

      <div className="admin-page">

        {/* ══ HERO ══ */}
        <div className="admin-hero">
          <h1 className="admin-title">⚡ Admin Dashboard</h1>
          <p className="admin-sub">Manage users, blogs, and rewards</p>
          <Link
            to="/activity"
            style={{
              display: "inline-block",
              marginTop: "14px",
              padding: "10px 20px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #6332ff, #4c1fd7)",
              color: "#fff",
              fontWeight: 600,
              fontSize: "14px",
              textDecoration: "none",
              boxShadow: "0 4px 16px rgba(99, 50, 255, 0.35)",
            }}
          >
            📊 Open Activity Dashboard →
          </Link>
          {/* ══ REFER & EARN MANAGEMENT (STEP 19) ══
              Its own full page rather than an embedded section: the referral
              queue, the fraud queue and the payout queue are each paginated
              work lists, and they need the room. */}
          <Link
            to="/refer-admin"
            style={{
              display: "inline-block",
              marginTop: "14px",
              marginLeft: "10px",
              padding: "10px 20px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #8b5cf6, #06b6d4)",
              color: "#fff",
              fontWeight: 600,
              fontSize: "14px",
              textDecoration: "none",
              boxShadow: "0 4px 16px rgba(139, 92, 246, 0.35)",
            }}
          >
            🎁 Refer &amp; Earn Management →
          </Link>
          {/* ══ PRO SYSTEM (STEPS 26, 27) ══
              Its own full page for the same reason as Refer & Earn above: the
              five sections (Pro Users, Pro Creators, Payments & Earnings,
              Payouts, Transactions) are wide work lists and need the room.
              A new link only — nothing on this dashboard changed. */}
          <Link
            to="/admin-pro"
            style={{
              display: "inline-block",
              marginTop: "14px",
              marginLeft: "10px",
              padding: "10px 20px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #7c3aed, #a78bfa)",
              color: "#fff",
              fontWeight: 600,
              fontSize: "14px",
              textDecoration: "none",
              boxShadow: "0 4px 16px rgba(124, 58, 237, 0.35)",
            }}
          >
            💎 Pro System →
          </Link>
          {/* ══ BUY ME A TREAT — transactions, payouts, verification, treats,
              reported greetings ══ A new link only. */}
          <Link
            to="/admin-treats"
            style={{
              display: "inline-block",
              marginTop: "14px",
              marginLeft: "10px",
              padding: "10px 20px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #db2777, #8b5cf6)",
              color: "#fff",
              fontWeight: 600,
              fontSize: "14px",
              textDecoration: "none",
              boxShadow: "0 4px 16px rgba(219, 39, 119, 0.35)",
            }}
          >
            🎁 Buy Me a Treat →
          </Link>
          {/* ══ ADVERTISING MANAGEMENT ══
              Its own full page (/ads-admin) — campaigns, review queue,
              pricing and reports. Also still embedded at the bottom. */}
          <Link
            to="/ads-admin"
            style={{
              display: "inline-block",
              marginTop: "14px",
              marginLeft: "10px",
              padding: "10px 20px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #7c3aed, #06b6d4)",
              color: "#fff",
              fontWeight: 600,
              fontSize: "14px",
              textDecoration: "none",
              boxShadow: "0 4px 16px rgba(6, 182, 212, 0.35)",
            }}
          >
            📢 Advertising Management →
          </Link>
          {/* ══ MANAGE LINKS ══
              Smart Links on every Nth blog card — links, card interval,
              redirect cooldown and click analytics (/smart-links-admin). */}
          <Link
            to="/smart-links-admin"
            style={{
              display: "inline-block",
              marginTop: "14px",
              marginLeft: "10px",
              padding: "10px 20px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #0ea5e9, #22c55e)",
              color: "#fff",
              fontWeight: 600,
              fontSize: "14px",
              textDecoration: "none",
              boxShadow: "0 4px 16px rgba(14, 165, 233, 0.35)",
            }}
          >
            🔗 Manage Links →
          </Link>
        </div>

        {/* ══ STATS ══ */}
        <div className="admin-stats-row">
          {[
            { icon: "👥", num: users.length,   label: "Total Users"  },
            { icon: "🚫", num: totalBlocked,   label: "Blocked"      },
            { icon: "📝", num: blogs.length,   label: "Total Blogs"  },
            { icon: "❤️", num: totalLikes,     label: "Total Likes"  },
            { icon: "💰", num: `₹${totalRewards}`, label: "Rewards Given" },
          ].map(s => (
            <div key={s.label} className="admin-stat-card">
              <span className="admin-stat-icon">{s.icon}</span>
              <span className="admin-stat-num">{s.num}</span>
              <span className="admin-stat-label">{s.label}</span>
            </div>
          ))}
        </div>

        {/* ══ ANNOUNCEMENT SECTION ══ */}
        <div className="admin-section">
          <div className="admin-section-header">
            <h2 className="admin-section-title">📢 Send Announcement</h2>
          </div>
          <div className="admin-announce">
            <input
              className="admin-search admin-announce-title"
              placeholder="Title (optional) — e.g. Server Maintenance"
              value={annTitle}
              onChange={(e) => setAnnTitle(e.target.value)}
            />
            <textarea
              className="admin-announce-body"
              placeholder="Announcement message (sent as a push notification)…"
              value={annBody}
              onChange={(e) => setAnnBody(e.target.value)}
              rows={3}
            />
            <input
              className="admin-search admin-announce-title"
              placeholder="Link (optional) — e.g. https://amazon.com/… (tapping opens it)"
              value={annLink}
              onChange={(e) => setAnnLink(e.target.value)}
            />

            {/* ── RECIPIENT SELECTION ── */}
            <div className="admin-recipients">
              <div className="admin-recipients-modes">
                <label className="admin-recipient-mode">
                  <input
                    type="radio"
                    name="ann-mode"
                    checked={annSendToAll}
                    onChange={() => setAnnSendToAll(true)}
                  />
                  <span>Send to everyone</span>
                </label>
                <label className="admin-recipient-mode">
                  <input
                    type="radio"
                    name="ann-mode"
                    checked={!annSendToAll}
                    onChange={() => setAnnSendToAll(false)}
                  />
                  <span>Select specific users</span>
                </label>
                <span className="admin-recipient-count">
                  {annSendToAll
                    ? `All active users (${annActiveCount})`
                    : `${annSelectedIds.length} user${annSelectedIds.length === 1 ? "" : "s"} selected`}
                </span>
              </div>

              {!annSendToAll && (
                <div className="admin-recipient-picker">
                  <input
                    className="admin-search"
                    placeholder="Search users to add…"
                    value={annUserSearch}
                    onChange={(e) => setAnnUserSearch(e.target.value)}
                  />
                  <div className="admin-recipient-bulk">
                    <button
                      type="button"
                      className="admin-btn btn-success"
                      onClick={() => setAnnSelectedIds(users.map((u) => u._id))}
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      className="admin-btn btn-warn"
                      onClick={() => setAnnSelectedIds([])}
                    >
                      Clear
                    </button>
                  </div>
                  <div className="admin-recipient-list">
                    {annFilteredUsers.length === 0 ? (
                      <div className="admin-empty">No users found</div>
                    ) : (
                      annFilteredUsers.map((u) => (
                        <label key={u._id} className="admin-recipient-item">
                          <input
                            type="checkbox"
                            checked={annSelectedIds.includes(u._id)}
                            onChange={() => toggleAnnUser(u._id)}
                          />
                          <span className="admin-recipient-name">{u.name || "—"}</span>
                          <span className="admin-recipient-email">{u.email}</span>
                        </label>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="admin-action-group">
              <button
                className="admin-btn btn-success"
                onClick={sendAnnouncement}
                disabled={!annCanSend}
              >
                {annSending
                  ? "Sending…"
                  : annSendToAll
                  ? `📢 Broadcast to All (${annActiveCount})`
                  : `📢 Send to ${annSelectedIds.length} user${annSelectedIds.length === 1 ? "" : "s"}`}
              </button>
              {annResult && <span className="admin-announce-result">{annResult}</span>}
            </div>
          </div>
        </div>

        {/* ══ USERS SECTION ══ */}
        <div className="admin-section">
          <div className="admin-section-header">
            <h2 className="admin-section-title">👥 Users</h2>
            <input
              className="admin-search"
              placeholder="Search users..."
              value={userSearch}
              onChange={e => { setUserSearch(e.target.value); setUserPage(1); }}
            />
          </div>

          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Registered On</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedUsers.length === 0 ? (
                  <tr><td colSpan={6} className="admin-empty">No users found</td></tr>
                ) : pagedUsers.map((user, i) => (
                  <tr key={user._id}>
                    <td className="admin-td-num">{(userPage - 1) * usersPerPage + i + 1}</td>
                    <td>
                      <span className="admin-user-name">{user.name || "—"}</span>
                    </td>
                    <td className="admin-td-muted">{user.email}</td>
                    <td className="admin-td-muted">
                      {user.createdAt
                        ? new Date(user.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
                        : "—"}
                    </td>
                    <td>
                      <span className={`admin-badge ${user.blocked ? "badge-blocked" : "badge-active"}`}>
                        {user.blocked ? "Blocked" : "Active"}
                      </span>
                    </td>
                    <td>
                      <div className="admin-action-group">
                        <button
                          className={`admin-btn ${user.blocked ? "btn-success" : "btn-warn"}`}
                          onClick={() => toggleBlock(user._id)}
                        >
                          {user.blocked ? "Unblock" : "Block"}
                        </button>
                        <button className="admin-btn btn-danger" onClick={() => deleteUser(user._id)}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={userPage} total={totalUserPages} onChange={setUserPage} />
        </div>

        {/* ══ ALL BLOGS SECTION ══ */}
        <div className="admin-section">
          <div className="admin-section-header">
            <h2 className="admin-section-title">📝 All Blogs</h2>
            <input
              className="admin-search"
              placeholder="Search by title or author name..."
              value={blogSearch}
              onChange={e => { setBlogSearch(e.target.value); setBlogPage(1); }}
            />
          </div>

          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Title</th>
                  <th>Author</th>
                  <th>Image</th>
                  <th>Video</th>
                  <th>Date</th>
                  <th>❤️ Likes</th>
                  <th>🔥 Streak</th>
                  <th>💰 Reward</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedBlogs.length === 0 ? (
                  <tr><td colSpan={10} className="admin-empty">No blogs found</td></tr>
                ) : pagedBlogs.map((blog, i) => {
                  const rewardId   = getRewardId(blog._id);
                  const totalRew   = getBlogReward(blog._id);
                  const authorName = getAuthorName(blog.author);
                  const streak     = getBlogStreak(blog);

                  return (
                    <tr key={blog._id}>
                      <td className="admin-td-num">{(blogPage - 1) * blogsPerPage + i + 1}</td>
                      <td>
                        <div className="admin-blog-title">{blog.title || "—"}</div>
                      </td>
                      <td>
                        <span className="admin-author-chip">{authorName}</span>
                      </td>
                      <td>
                        {blog.image
                          ? <img src={blog.image} className="admin-thumb" alt="" />
                          : <span className="admin-td-muted">—</span>}
                      </td>
                      <td>
                        {blog.video
                          ? <a className="admin-video-link" href={blog.video} target="_blank" rel="noreferrer">▶ View</a>
                          : <span className="admin-td-muted">—</span>}
                      </td>
                      <td className="admin-td-muted">
                        {new Date(blog.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                      </td>
                      <td>
                        <span className="admin-likes-badge">❤️ {blog.likes?.length || 0}</span>
                      </td>
                      <td>
                        <span className="admin-streak-badge">🔥 {streak}</span>
                      </td>
                      <td>
                        <span className={`admin-reward-badge ${totalRew > 0 ? "reward-has" : "reward-none"}`}>
                          ₹{totalRew}
                        </span>
                      </td>
                      <td>
                        <div className="admin-action-group">
                          <button className="admin-btn btn-success" onClick={() => addReward(blog._id)}>
                            💰 Reward
                          </button>
                          {rewardId && (
                            <button className="admin-btn btn-warn" onClick={() => deleteReward(rewardId)}>
                              Remove
                            </button>
                          )}
                          <button className="admin-btn btn-danger" onClick={() => deleteBlog(blog._id)}>
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pagination page={blogPage} total={totalBlogPages} onChange={setBlogPage} />
        </div>

        {/* ══ SALES EXECUTIVE MANAGEMENT ══
            Embedded here (same pattern as the sections below) so the Sales
            Executive counters and controls live on the Admin Dashboard itself;
            it is also reachable as its own page at /sales-admin. */}
        <AdminSalesExecutives embedded />

        {/* ══ OTHER SECTIONS ══ */}
        <AdSubmit />
        <AdsAnalytics />
        {/* ══ ADVERTISING SYSTEM — pricing + campaign review ══ */}
        <AdminAdCampaigns />
        <AdminLove />

      </div>
    </div>
  );
}

export default AdminDashboard;